import { useCallback, useEffect, useState } from 'react';
import { fetchContactSettings } from '../api';

let cachedSettings = null;
let cacheIsPopulated = false;
let settingsRequest = null;

function loadContactSettings() {
  if (cacheIsPopulated) return Promise.resolve(cachedSettings);

  if (!settingsRequest) {
    settingsRequest = fetchContactSettings()
      .then((settings) => {
        cachedSettings = settings;
        // Flagged separately so an empty payload is still treated as loaded.
        cacheIsPopulated = true;
        return settings;
      })
      .finally(() => {
        settingsRequest = null;
      });
  }

  return settingsRequest;
}

/**
 * Drops the shared cache so the next mount (or reload) refetches the copy.
 * Used by the retry affordance and by tests to keep cases isolated.
 */
export function resetContactSettingsCache() {
  cachedSettings = null;
  cacheIsPopulated = false;
  settingsRequest = null;
}

function initialState() {
  return cacheIsPopulated
    ? { settings: cachedSettings, isLoading: false, error: null }
    : { settings: null, isLoading: true, error: null };
}

/**
 * Loads contact copy once per application lifetime and shares in-flight requests
 * between the standalone page and stop-page panel.
 *
 * Consumers must wait for `isLoading` to settle before rendering inputs: the
 * configured field set decides which values are submitted, so rendering a
 * default form first would let the user type into fields the filiale removed.
 */
export function useContactSettings() {
  const [state, setState] = useState(initialState);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    loadContactSettings()
      .then((loadedSettings) => {
        if (!cancelled) setState({ settings: loadedSettings, isLoading: false, error: null });
      })
      .catch((error) => {
        console.warn('[Contact] Failed to load contact settings.', error);
        if (!cancelled) setState({ settings: null, isLoading: false, error });
      });

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const reload = useCallback(() => {
    resetContactSettingsCache();
    setState({ settings: null, isLoading: true, error: null });
    setReloadToken((token) => token + 1);
  }, []);

  return { ...state, reload };
}
